import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { api } from '../services/api';
import { colors } from '../styles';

const GraphContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
}));

const ScrollableContainer = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'hidden',
  paddingBottom: theme.spacing(1),
  '&::-webkit-scrollbar': {
    height: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb': {
    background: colors.primary,
    borderRadius: 3,
  },
}));

const GraphGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateRows: 'repeat(7, 12px)', // 7 rows for days of week
  gridAutoColumns: 12,
  gridAutoFlow: 'column',
  gap: 4,
  alignContent: 'start', // Align grid content to start
  width: 'max-content', // Allow grid to expand beyond container width
}));

// WeekColumn removed - using grid directly

const DaySquare = styled(Box)(({ intensity, spending }) => {
  const pinkShades = [
    '#ffe3e7', // Light pink (0-33%)
    '#ffccd6', // Medium-light pink (33-66%)
    '#ffa1b5', // Medium-dark pink (66-99%)
    '#FF5D82', // Dark pink - brand color (100%)
  ];
  
  // Show soft gray for days with $0 spending
  let bgColor;
  if (spending === 0) {
    bgColor = '#efefef';
  } else if (intensity === 0) {
    bgColor = pinkShades[0];
  } else {
    // Map intensity (0-1) to available pink shades based on array length
    bgColor = pinkShades[Math.min(Math.floor(intensity * pinkShades.length), pinkShades.length - 1)];
  }
  
  return {
    width: 12,
    height: 12,
    borderRadius: 3,
    backgroundColor: bgColor,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.2)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
  };
});

const GraphWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
}));

const DayLabels = styled(Box)(() => ({
  display: 'grid',
  gridTemplateRows: 'repeat(7, 12px)', // 7 rows for days of week
  gap: 4,
  marginRight: 8,
  fontSize: '10px',
  color: 'text.secondary',
  marginTop: 24, // Match month labels height (20px) + marginBottom (4px)
  alignContent: 'start', // Align grid content to start
}));

const Legend = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  fontSize: '12px',
  color: 'text.secondary',
}));

const LegendSquare = styled(Box)(({ color }) => ({
  width: 12,
  height: 12,
  borderRadius: 3,
  backgroundColor: color,
}));

const SpendingGraph = ({ days, onHasData, refreshKey }) => {
  const [loading, setLoading] = useState(true);
  const [spendingData, setSpendingData] = useState(null);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const fetchSpendingData = async () => {
      try {
        setLoading(true);
        // Request all available data (up to 2 years / 730 days)
        // The graph will show all returned data and allow horizontal scrolling
        const response = await api.get('/transactions/daily-spending', {
          params: { days: days || 730 }
        });
        const data = response.data.data;
        setSpendingData(data);
        setError(null);
        
        // Notify parent once the user has any transactions
        const hasSpendingTransactions = data.maxSpending > 0 || Object.keys(data.dailySpending).length > 0;
        const hasAnyTransactions =
          hasSpendingTransactions ||
          (typeof data.totalTransactions === 'number' && data.totalTransactions > 0);
        
        if (onHasData) {
          onHasData(hasAnyTransactions);
        }
        
        // Scroll to the end (latest date) after data loads
        // Use setTimeout to ensure DOM has updated
        setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
          }
        }, 100);
      } catch (err) {
        console.error('Error fetching spending data:', err);
        setError('Failed to load spending data');
        if (onHasData) {
          onHasData(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSpendingData();
  }, [days, refreshKey, onHasData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!spendingData) {
    return null;
  }

  const hasSpendingData = spendingData.maxSpending > 0 || Object.keys(spendingData.dailySpending).length > 0;
  const hasAnyTransactions =
    hasSpendingData ||
    (typeof spendingData.totalTransactions === 'number' && spendingData.totalTransactions > 0);

  if (!hasAnyTransactions) {
    return null;
  }
  if (!hasSpendingData) {
    return (
      <GraphContainer>
        <Typography variant="body2" color="text.secondary">
          No spending data available yet. Once you have completed transactions, your spending activity will appear here.
        </Typography>
      </GraphContainer>
    );
  }

  // Build graph keys from backend date range (already in user's timezone)
  // The backend groups dailySpending by the user's configured timezone before sending it to the client
  // We should use those date keys as-is without additional timezone conversion
  // Show all available data - users can scroll horizontally to see the full history
  
  // Use backend's startDate and endDate to ensure we show ALL days in the range,
  // not just days with transactions. This ensures weekends and other days without
  // transactions still appear in the grid.
  const gridStartDateKey = spendingData.startDate || null;
  const gridEndDateKey = spendingData.endDate || null;
  
  // Helper to add days to a date string (YYYY-MM-DD)
  // Date keys from backend represent dates in user's timezone, not UTC
  // Parse as local date to preserve correct day of week
  function addDays(dateStr, days) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  
  // Helper to get day of week from date string (YYYY-MM-DD)
  // Date keys represent dates in user's timezone, parse as local date
  function getDayOfWeek(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.getDay(); // 0 = Sunday, 6 = Saturday
  }
  
  // Build all date keys for grid, including padding for full weeks (Sunday to Saturday)
  // Show all available data from backend - no limit based on container width
  let allDateKeys = [];
  
  if (gridStartDateKey && gridEndDateKey) {
    // Find the day of week for start (0=Sunday)
    // Use local date parsing since date keys are in user's timezone
    const startDayOfWeek = getDayOfWeek(gridStartDateKey);
    
    // Pad at start to Sunday
    let gridStart = gridStartDateKey;
    if (startDayOfWeek !== 0) {
      gridStart = addDays(gridStartDateKey, -startDayOfWeek);
    }
    
    // Find the day of week for end
    const endDayOfWeek = getDayOfWeek(gridEndDateKey);
    
    // Pad at end to Saturday
    let gridEnd = gridEndDateKey;
    if (endDayOfWeek !== 6) {
      gridEnd = addDays(gridEndDateKey, 6 - endDayOfWeek);
    }
    
    // Build all date keys from gridStart to gridEnd
    // This will include all available data (up to 2 years) - users can scroll horizontally
    let currentDateKey = gridStart;
    while (currentDateKey <= gridEnd) {
      allDateKeys.push(currentDateKey);
      currentDateKey = addDays(currentDateKey, 1);
    }
  }
  
  // Use backend's endDate as "today" reference to avoid timezone mismatch
  // The backend calculates dates in the user's configured timezone, and we need to compare
  // date keys in the same timezone system. Using the browser's local date would cause
  // mismatches if the user's configured timezone differs from their browser timezone.
  const todayKey = gridEndDateKey;
  
  // Organize by weeks (Sunday to Saturday)
  const weeks = [];
  let currentWeek = [];
  
  allDateKeys.forEach(dateKey => {
    // Compare date strings directly - backend and frontend now use the same timezone basis
    const isFuture = dateKey > todayKey;
    
    // Only include spending data for dates up to today
    const spending = isFuture ? 0 : (spendingData.dailySpending[dateKey] || 0);
    const intensity = spendingData.maxSpending > 0 ? spending / spendingData.maxSpending : 0;
    
    // Create Date object for display purposes
    // Parse as local date since dateKey represents a date in user's timezone
    const [year, month, day] = dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    currentWeek.push({
      date,
      dateKey,
      spending,
      intensity,
      isFuture,
    });

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });
  
  // Add remaining days to last week if needed
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  // Generate month labels by finding which week contains the 1st of each month
  const monthLabels = new Map();
  const seenMonthKeys = new Set();
  
  // Iterate through all weeks and find the first week that contains the 1st of each month
  weeks.forEach((week, weekIndex) => {
    // Check each day in this week to see if it's the 1st of a month
    for (const day of week) {
      if (day && day.date && !day.isFuture) {
        const date = new Date(day.date);
        if (date.getDate() === 1) {
          const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
          if (!seenMonthKeys.has(monthKey)) {
            seenMonthKeys.add(monthKey);
            monthLabels.set(weekIndex, date.toLocaleDateString('en-US', { month: 'short' }));
            break; // Only one label per week
          }
        }
      }
    }
  });

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const legendColors = [
    '#efefef', // Gray ($0 spending)
    '#ffe3e7', // Light pink (0-33%)
    '#ffccd6', // Medium-light pink (33-66%)
    '#ffa1b5', // Medium-dark pink (66-99%)
    '#FF5D82', // Dark pink - brand color (100%)
  ];

  return (
    <GraphContainer ref={containerRef}>
      <GraphWrapper>
        {/* Day labels column */}
        <DayLabels>
          {dayLabels.map((label, index) => (
            <Box 
              key={label} 
              sx={{ 
                height: 12, 
                display: 'flex', 
                alignItems: 'center',
                lineHeight: '12px',
                fontSize: '10px'
              }}
            >
              {index % 2 === 1 ? label : ''}
            </Box>
          ))}
        </DayLabels>

        {/* Graph area */}
        <Box flex={1} display="flex" flexDirection="column" sx={{ overflow: 'hidden' }}>
          {/* Graph grid - wrapped in scrollable container */}
          <ScrollableContainer ref={scrollContainerRef}>
            {/* Month labels row - positioned absolutely within scroll container to align with weeks */}
            <Box 
              sx={{ 
                position: 'relative',
                width: 'max-content',
                mb: 0.5,
                height: 20,
              }}
            >
              {weeks.map((_, weekIndex) => (
                <Box
                  key={`month-label-${weekIndex}`}
                  sx={{
                    position: 'absolute',
                    left: weekIndex * (12 + 4), // 12px column width + 4px gap
                    width: 12,
                    display: 'flex',
                    justifyContent: 'flex-start',
                  }}
                >
                  {monthLabels.has(weekIndex) && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      {monthLabels.get(weekIndex)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
            
            <GraphGrid>
              {weeks.map((week, weekIndex) =>
                week.map((day, dayIndex) => {
                    if (!day) {
                    return <Box key={`empty-${weekIndex}-${dayIndex}`} sx={{ width: 12, height: 12 }} />;
                  }

                  // Future dates should appear as empty squares
                  if (day.isFuture) {
                    return <Box key={day.dateKey} sx={{ width: 12, height: 12 }} />;
                    }

                    const formattedAmount = (day.spending / 100).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    });

                    return (
                      <Tooltip
                        key={day.dateKey}
                        title={
                          <Box>
                            <Typography variant="caption" display="block">
                              {day.date.toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </Typography>
                            <Typography variant="caption" fontWeight="bold">
                              {day.spending > 0 ? formattedAmount : 'No spending'}
                            </Typography>
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                      <DaySquare 
                        intensity={day.intensity} 
                        spending={day.spending}
                        role="gridcell"
                        aria-label={`${day.date.toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}: ${day.spending > 0 ? formattedAmount : 'No spending'}`}
                      />
                      </Tooltip>
                    );
                })
              )}
            </GraphGrid>
          </ScrollableContainer>
        </Box>
      </GraphWrapper>
      
      <Legend>
        <Typography variant="caption" sx={{ mr: 1 }}>
          Less
        </Typography>
        {legendColors.map((color, index) => (
          <LegendSquare key={index} color={color} />
        ))}
        <Typography variant="caption" sx={{ ml: 1 }}>
          More
        </Typography>
      </Legend>
    </GraphContainer>
  );
};

export default SpendingGraph;
